import type { StoreItemGroupTreeNode } from './useStoreItemGroups';
import type { StoreItem } from './useData';

export type GroupTreeRow = StoreItemGroupTreeNode & {
  depth: number;
  hasChildren: boolean;
};

export const flattenGroupTree = (
  nodes: StoreItemGroupTreeNode[],
  collapsedIds: Set<number>,
  depth = 0,
): GroupTreeRow[] => {
  const rows: GroupTreeRow[] = [];
  for (const node of nodes) {
    const hasChildren = (node.children?.length ?? 0) > 0;
    rows.push({ ...node, depth, hasChildren });
    if (hasChildren && !collapsedIds.has(node.id)) {
      rows.push(...flattenGroupTree(node.children, collapsedIds, depth + 1));
    }
  }
  return rows;
};

const walkFilterTree = (
  nodes: StoreItemGroupTreeNode[],
  selfMatch: (node: StoreItemGroupTreeNode) => boolean,
): StoreItemGroupTreeNode[] => {
  const walk = (node: StoreItemGroupTreeNode): StoreItemGroupTreeNode | null => {
    const filteredChildren = (node.children ?? [])
      .map(walk)
      .filter((child): child is StoreItemGroupTreeNode => child != null);
    if (selfMatch(node) || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };
  return nodes.map(walk).filter((node): node is StoreItemGroupTreeNode => node != null);
};

export const filterGroupTree = (nodes: StoreItemGroupTreeNode[], term: string): StoreItemGroupTreeNode[] => {
  const q = term.trim().toLowerCase();
  if (!q) return nodes;
  return walkFilterTree(
    nodes,
    (node) =>
      node.name.toLowerCase().includes(q) ||
      (node.abrv?.toLowerCase().includes(q) ?? false),
  );
};

export const findGroupNodeById = (
  nodes: StoreItemGroupTreeNode[],
  id: number,
): StoreItemGroupTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findGroupNodeById(node.children ?? [], id);
    if (found) return found;
  }
  return null;
};

export const getGroupPath = (
  nodes: StoreItemGroupTreeNode[],
  id: number,
): StoreItemGroupTreeNode[] => {
  const walk = (list: StoreItemGroupTreeNode[], trail: StoreItemGroupTreeNode[]): StoreItemGroupTreeNode[] | null => {
    for (const node of list) {
      const next = [...trail, node];
      if (node.id === id) return next;
      const found = walk(node.children ?? [], next);
      if (found) return found;
    }
    return null;
  };
  return walk(nodes, []) ?? [];
};

export const collectExpandableGroupIds = (nodes: StoreItemGroupTreeNode[]): number[] => {
  const ids: number[] = [];
  const walk = (list: StoreItemGroupTreeNode[]) => {
    for (const node of list) {
      if ((node.children?.length ?? 0) > 0) {
        ids.push(node.id);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
};

/** Collapse everything below roots (roots stay expanded). */
export const collectCollapsedIdsBelowRoot = (nodes: StoreItemGroupTreeNode[]): number[] => {
  const ids: number[] = [];
  const walk = (list: StoreItemGroupTreeNode[], isRoot: boolean) => {
    for (const node of list) {
      if (!isRoot && (node.children?.length ?? 0) > 0) ids.push(node.id);
      walk(node.children ?? [], false);
    }
  };
  walk(nodes, true);
  return ids;
};

const collectDescendantGroupIds = (node: StoreItemGroupTreeNode): number[] => {
  const ids = [node.id];
  for (const child of node.children ?? []) {
    ids.push(...collectDescendantGroupIds(child));
  }
  return ids;
};

/** Direct item count for a group (not including descendants). */
export const countDirectItemsInGroup = (items: StoreItem[], groupId: number): number =>
  items.reduce((n, item) => (item.groupId === groupId ? n + 1 : n), 0);

export const filterItemsForGroup = (
  items: StoreItem[],
  group: StoreItemGroupTreeNode | null,
  includeSubgroups: boolean,
  searchTerm: string,
): StoreItem[] => {
  if (!group) return [];
  const allowed = new Set(
    includeSubgroups ? collectDescendantGroupIds(group) : [group.id],
  );
  const q = searchTerm.trim().toLowerCase();
  return items.filter((item) => {
    if (item.groupId == null || !allowed.has(item.groupId)) return false;
    if (!q) return true;
    return (
      item.name.toLowerCase().includes(q) ||
      (item.vendorCode?.toLowerCase().includes(q) ?? false) ||
      (item.notes?.toLowerCase().includes(q) ?? false) ||
      (item.unit?.ucode?.toLowerCase().includes(q) ?? false)
    );
  });
};
